/**
 * Defang IOCs for safe display.
 *
 * Malicious URLs and IOCs in threat feeds are NEVER shown fanged — users
 * could accidentally click, paste or copy them into a live browser. We follow
 * the SOC-standard defanging conventions:
 *
 *   http://  → hxxp://
 *   https:// → hxxps://
 *   .        → [.]     (only inside domain/path, not protocol scheme)
 *   @        → [@]
 *
 * For IPv4: 1.2.3.4 → 1[.]2[.]3[.]4
 * For domains inside raw strings, we only defang dots that appear between
 * word characters (not in numeric contexts like CVE-2024.1.1 unless clearly a
 * URL).
 */

export function defangUrl(url: string): string {
  if (!url) return url
  let s = url
  // Scheme
  s = s.replace(/^https:\/\//i, 'hxxps://')
  s = s.replace(/^http:\/\//i, 'hxxp://')
  // Defang dots after the scheme (avoid protocol dots like 'hxxp://')
  const schemeMatch = s.match(/^(hxxps?:\/\/)(.*)$/i)
  if (schemeMatch) {
    const [, scheme, rest] = schemeMatch
    s = scheme + rest.replace(/\./g, '[.]').replace(/@/g, '[@]')
  } else {
    // No scheme — treat the whole string as a host / path; defang dots
    s = s.replace(/\./g, '[.]').replace(/@/g, '[@]')
  }
  return s
}

export function defangIp(ip: string): string {
  if (!ip) return ip
  return ip.replace(/\./g, '[.]').replace(/:/g, '[:]')
}

export function defangDomain(domain: string): string {
  if (!domain) return domain
  return domain.replace(/\./g, '[.]').replace(/@/g, '[@]')
}

/** Generic — detects URL-ish / IP-ish / domain-ish and defangs appropriately. */
export function defangIoc(ioc: string, iocType?: string): string {
  if (!ioc) return ioc
  const t = (iocType || '').toLowerCase()
  if (t.includes('ip')) return defangIp(ioc)
  if (t.includes('url') || ioc.match(/^https?:\/\//i)) return defangUrl(ioc)
  if (t.includes('domain') || t.includes('host')) return defangDomain(ioc)
  // Hash or unknown — return as-is (hashes are safe to display)
  if (ioc.match(/^[a-f0-9]{32,64}$/i)) return ioc
  // Default: if it contains dots and looks like a host, defang
  if (ioc.match(/[a-z]\.[a-z]/i)) return defangDomain(ioc)
  return ioc
}

/** Re-fang for copy — user must confirm before writing to clipboard. */
export function refang(s: string): string {
  if (!s) return s
  return s
    .replace(/\[\.\]/g, '.')
    .replace(/\[@\]/g, '@')
    .replace(/\[:\]/g, ':')
    .replace(/^hxxps:\/\//i, 'https://')
    .replace(/^hxxp:\/\//i, 'http://')
}

/** Copy to clipboard with explicit user confirm. Returns true if copied. */
export async function copyRefanged(defanged: string): Promise<boolean> {
  const real = refang(defanged)
  const ok = window.confirm(
    `Copy the LIVE (un-defanged) indicator to clipboard?\n\n${real}\n\nClick OK only if you intend to use this in a controlled environment (analysis sandbox, blocklist, SIEM rule).`
  )
  if (!ok) return false
  try {
    await navigator.clipboard.writeText(real)
    return true
  } catch {
    return false
  }
}
